/**
 * Late auto-gratuity — the SERVER-OWNED projection on `order.lateAutoGratuity`
 * and the append-only events under `orders/{id}/late_gratuity_events/{key}`.
 *
 * SSOT: docs/LATE_AUTO_GRATUITY_ARCHITECTURE.md.
 *
 * WHAT IT IS. The restaurant's auto-gratuity applies to a party of N or more.
 * The till places it on the checks itself while a check can still be re-priced
 * (holds no money). When the party reaches the threshold only AFTER every check
 * already holds money, no check may be re-priced, so the gratuity is OWED and
 * must be either COLLECTED (added to a check that can still take money, as a
 * further payment) or WAIVED (by a manager, with a PIN and a reason).
 *
 * WHO WRITES IT. Only the API (`LateGratuityService`, Admin SDK), in one
 * Firestore transaction per action. `firestore.rules` refuses every client
 * write that sets, changes or removes it, and the events are server-write-only,
 * so a till write can never erase a waiver's audit (the defect that sank the
 * till-owned design, POS PR #728).
 *
 * THE RULE IS FROZEN AT FIRST DETECTION. `ruleSnapshot` records the rate,
 * threshold and service-tax rate the server read the first time it found the
 * gratuity owed. Every later derivation uses the snapshot, never the live
 * configuration — a config change after the fact must not invent an amount
 * owed on an order the guest was already presented.
 *
 * MONEY IS IN MINOR UNITS (cents) here; check documents keep dollars.
 *
 * Every parser is defensive: a malformed map reads as ABSENT rather than
 * throwing, because this rides on every order snapshot.
 */

import { parseTaxExemptionActor, type TaxExemptionActor } from './TaxExemption';

/** Longest waiver reason kept. */
export const LATE_GRATUITY_REASON_MAX = 200;
/** Shortest waiver reason accepted (trimmed). */
export const LATE_GRATUITY_REASON_MIN = 3;

/**
 * The operator actions. Detection is not an action — it is a derivation.
 * `reapply` puts back a collected line a stale till write reverted (it moves
 * no new money: it restores what the server already recorded as collected).
 */
export const LATE_GRATUITY_ACTIONS = ['collect', 'waive', 'reapply'] as const;
export type LateGratuityAction = (typeof LATE_GRATUITY_ACTIONS)[number];

/**
 * Every event an order's history can hold: the operator actions plus the
 * server's own `release` (the party fell below the frozen threshold and a
 * collected line the till removed no longer applies — round 4).
 */
export const LATE_GRATUITY_EVENT_ACTIONS = [...LATE_GRATUITY_ACTIONS, 'release'] as const;
export type LateGratuityEventAction = (typeof LATE_GRATUITY_EVENT_ACTIONS)[number];

/**
 * Where the late gratuity stands.
 *   - `owed`                 — owed, nothing collected yet.
 *   - `partially_collected`  — some was collected and more is owed again (the
 *                              order grew after a collection).
 *   - `collected`            — nothing owed; it was collected.
 *   - `waived`               — nothing owed; it was waived (and none collected).
 *   - `cleared`              — nothing owed and nobody acted: it stopped
 *                              applying (the party shrank, the order shrank).
 */
export const LATE_GRATUITY_STATUSES = ['owed', 'partially_collected', 'collected', 'waived', 'cleared'] as const;
export type LateGratuityStatus = (typeof LATE_GRATUITY_STATUSES)[number];

/** Statuses the till must show a notice (and a pay/close warning) for. */
export function lateGratuityIsOutstanding(status: LateGratuityStatus | undefined): boolean {
  return status === 'owed' || status === 'partially_collected';
}

/** Who acted — resolved by the API from the restaurant's employee records. */
export type LateGratuityActor = TaxExemptionActor;

/** The auto-gratuity rule as the server read it at first detection. */
export interface LateGratuityRuleSnapshot {
  readonly type: 'percentage' | 'flatAmount';
  /** Fraction (0.18 = 18%) for `percentage`. */
  readonly rate?: number;
  /** Flat amount in cents for `flatAmount`. */
  readonly amountMinor?: number;
  /** Party size at which it applies; 0 = always. */
  readonly threshold: number;
  /** Service tax on the gratuity, percentage points (0 when untaxed or the order is tax exempt). */
  readonly serviceTaxPercent: number;
  /** The configured label, e.g. "Auto Gratuity". */
  readonly description: string;
  /** The tax rate ids the service tax was summed from (for review only). */
  readonly taxRateIds: readonly number[];
}

/** The server-owned projection on `order.lateAutoGratuity`. */
export interface ILateAutoGratuity {
  readonly status: LateGratuityStatus;
  /** What is owed now, cents (gratuity only — its service tax is added on collection). */
  readonly owedMinor: number;
  /** Collected so far, cents, cumulative. */
  readonly collectedMinor: number;
  /** Waived so far, cents, cumulative. Counts as carried from then on. */
  readonly waivedMinor: number;
  /** Party size it was last derived at. */
  readonly partySize: number;
  /** When it was first found owed. */
  readonly detectedAt: Date;
  /** When the last waiver happened — the report date for a waiver. */
  readonly lastWaivedAt?: Date;
  readonly ruleSnapshot: LateGratuityRuleSnapshot;
  /** Id of the last event (= its idempotency key); absent until an operator acts. */
  readonly lastEventId?: string;
  /**
   * Collected late gratuity the order's checks no longer carry, as /detect last
   * found it (a stale till write reverted a line). Absent or 0 = none. A
   * WARNING, never an amount owed: what was collected is `collectedMinor`.
   */
  readonly driftMinor?: number;
  /**
   * Collected onto checks later voided or split away (or onto no check at all).
   * NOT drift — nothing can be reapplied there; shown so an operator knows
   * where that money was recorded. Absent or 0 = none.
   */
  readonly offCheckCollectedMinor?: number;
  readonly updatedAt: Date;
}

/** One operator action — `orders/{orderId}/late_gratuity_events/{idempotencyKey}`. */
export interface ILateGratuityEvent {
  readonly id: string;
  readonly action: LateGratuityEventAction;
  /** The gratuity collected or waived, cents. */
  readonly amountMinor: number;
  /** Service tax added with a collection, cents (0 for a waiver). */
  readonly serviceTaxMinor: number;
  /** Checks the collection was written onto, with each share. */
  readonly allocations: ReadonlyArray<{ checkGuid: string; gratuityMinor: number; serviceTaxMinor: number }>;
  readonly reason?: string;
  /** The employee signed in at the till / the user who pressed it. */
  readonly performedBy?: LateGratuityActor;
  /** For a waiver: the manager whose PIN approval the API consumed. */
  readonly approvedBy?: LateGratuityActor;
  readonly at: Date;
  readonly orderGuid: string;
  readonly orderNumber?: number;
  /**
   * sha256 of the request (action, expectedOwedMinor, reason, performer,
   * approver). A retry with the SAME key must match it, or it is refused —
   * a key never answers for a different request.
   */
  readonly requestFingerprint?: string;
  /** The order's projection as it stood after this event. */
  readonly statusAfter: LateGratuityStatus;
  readonly owedAfterMinor: number;
}

// ─── Parsers ────────────────────────────────────────────────────────────────

function toDate(value: unknown): Date | undefined {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  if (value && typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    const d = (value as { toDate: () => Date }).toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : undefined;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

function minor(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
}

function str(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t ? t.slice(0, max) : undefined;
}

export function parseLateGratuityRuleSnapshot(raw: unknown): LateGratuityRuleSnapshot | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (r.type !== 'percentage' && r.type !== 'flatAmount') return undefined;
  const threshold = minor(r.threshold);
  const serviceTaxPercent = typeof r.serviceTaxPercent === 'number' && Number.isFinite(r.serviceTaxPercent) && r.serviceTaxPercent >= 0
    ? r.serviceTaxPercent
    : undefined;
  if (threshold === undefined || serviceTaxPercent === undefined) return undefined;
  if (r.type === 'percentage' && !(typeof r.rate === 'number' && Number.isFinite(r.rate) && r.rate > 0)) return undefined;
  if (r.type === 'flatAmount' && minor(r.amountMinor) === undefined) return undefined;
  return {
    type: r.type,
    ...(r.type === 'percentage' ? { rate: r.rate as number } : { amountMinor: r.amountMinor as number }),
    threshold,
    serviceTaxPercent,
    description: str(r.description, 120) ?? 'Auto Gratuity',
    taxRateIds: Array.isArray(r.taxRateIds) ? r.taxRateIds.filter((n): n is number => typeof n === 'number' && Number.isInteger(n)) : [],
  };
}

export function parseLateAutoGratuity(raw: unknown): ILateAutoGratuity | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const status = (LATE_GRATUITY_STATUSES as readonly string[]).includes(r.status as string)
    ? (r.status as LateGratuityStatus)
    : undefined;
  const ruleSnapshot = parseLateGratuityRuleSnapshot(r.ruleSnapshot);
  const detectedAt = toDate(r.detectedAt);
  const owedMinor = minor(r.owedMinor);
  if (!status || !ruleSnapshot || !detectedAt || owedMinor === undefined) return undefined;
  const lastWaivedAt = toDate(r.lastWaivedAt);
  const lastEventId = str(r.lastEventId, 128);
  const driftMinor = minor(r.driftMinor);
  const offCheckCollectedMinor = minor(r.offCheckCollectedMinor);
  return {
    status,
    owedMinor,
    collectedMinor: minor(r.collectedMinor) ?? 0,
    waivedMinor: minor(r.waivedMinor) ?? 0,
    partySize: minor(r.partySize) ?? 0,
    detectedAt,
    ...(lastWaivedAt ? { lastWaivedAt } : {}),
    ruleSnapshot,
    ...(lastEventId ? { lastEventId } : {}),
    ...(driftMinor ? { driftMinor } : {}),
    ...(offCheckCollectedMinor ? { offCheckCollectedMinor } : {}),
    updatedAt: toDate(r.updatedAt) ?? detectedAt,
  };
}

export function parseLateGratuityEvent(id: string, raw: unknown): ILateGratuityEvent | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (!(LATE_GRATUITY_EVENT_ACTIONS as readonly unknown[]).includes(r.action)) return undefined;
  const amountMinor = minor(r.amountMinor);
  const at = toDate(r.at);
  const orderGuid = str(r.orderGuid, 128);
  const statusAfter = (LATE_GRATUITY_STATUSES as readonly string[]).includes(r.statusAfter as string)
    ? (r.statusAfter as LateGratuityStatus)
    : undefined;
  if (amountMinor === undefined || !at || !orderGuid || !statusAfter) return undefined;
  const allocations = Array.isArray(r.allocations)
    ? r.allocations.flatMap((a) => {
        if (!a || typeof a !== 'object') return [];
        const x = a as Record<string, unknown>;
        const checkGuid = str(x.checkGuid, 128);
        const gratuityMinor = minor(x.gratuityMinor);
        if (!checkGuid || gratuityMinor === undefined) return [];
        return [{ checkGuid, gratuityMinor, serviceTaxMinor: minor(x.serviceTaxMinor) ?? 0 }];
      })
    : [];
  const performedBy = parseTaxExemptionActor(r.performedBy);
  const approvedBy = parseTaxExemptionActor(r.approvedBy);
  const reason = str(r.reason, LATE_GRATUITY_REASON_MAX);
  const requestFingerprint = str(r.requestFingerprint, 128);
  const orderNumber = typeof r.orderNumber === 'number' ? r.orderNumber : undefined;
  return {
    id,
    action: r.action as LateGratuityEventAction,
    amountMinor,
    serviceTaxMinor: minor(r.serviceTaxMinor) ?? 0,
    allocations,
    ...(reason ? { reason } : {}),
    ...(performedBy ? { performedBy } : {}),
    ...(approvedBy ? { approvedBy } : {}),
    at,
    orderGuid,
    ...(orderNumber !== undefined ? { orderNumber } : {}),
    ...(requestFingerprint ? { requestFingerprint } : {}),
    statusAfter,
    owedAfterMinor: minor(r.owedAfterMinor) ?? 0,
  };
}

/**
 * The status a projection carries for these totals. ONE function, so the
 * service, the till's notice and the management card cannot disagree about
 * which word a set of numbers earns.
 */
export function lateGratuityStatusFor(input: {
  owedMinor: number;
  collectedMinor: number;
  waivedMinor: number;
}): LateGratuityStatus {
  if (input.owedMinor > 0) return input.collectedMinor > 0 ? 'partially_collected' : 'owed';
  if (input.collectedMinor > 0) return 'collected';
  if (input.waivedMinor > 0) return 'waived';
  return 'cleared';
}

// ─── Server-owned order fields ──────────────────────────────────────────────

/**
 * Order fields only their own SERVICE writes:
 * `taxExemption` (TaxExemptionService), `lateAutoGratuity`
 * (LateGratuityService) — both in a transaction — and `onlineOrderFeeProgram`
 * (stamped once by checkout; it decides whether OrderUp bills the order,
 * docs/ONLINE_SERVICE_FEE.md). `firestore.rules` keeps every client off them; an API
 * path that writes an order on a till's behalf (the offline projections) must
 * drop them too, or a stale read written back reverts the service's write —
 * and a till-supplied body could forge one.
 */
export const SERVER_OWNED_ORDER_FIELDS = ['taxExemption', 'lateAutoGratuity', 'onlineOrderFeeProgram'] as const;

/** `data` without the server-owned fields — for a set+merge, which then keeps the stored values. */
export function withoutServerOwnedOrderFields<T extends object>(data: T): Omit<T, (typeof SERVER_OWNED_ORDER_FIELDS)[number]> {
  const out = { ...data } as Record<string, unknown>;
  for (const key of SERVER_OWNED_ORDER_FIELDS) delete out[key];
  return out as Omit<T, (typeof SERVER_OWNED_ORDER_FIELDS)[number]>;
}
